import { MultiSelectOption } from '@/components/ui/multi-select-button';
import { ZipInsightItem } from '@/hooks/useConversationsInsights';
import React from 'react';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordian';
import { cn } from '@/lib/utils';
import NeutralInsightIcon from '../../public/icons/new-dashboard/neutral-insight.svg';
import DecreaseInsightIcon from '../../public/icons/new-dashboard/decrease-insight.svg';
import IncreaseInsightIcon from '../../public/icons/new-dashboard/increase-insight.svg';

const InsightIcon = {
  NEUTRAL: NeutralInsightIcon,
  NEGATIVE: DecreaseInsightIcon,
  POSITIVE: IncreaseInsightIcon,
};

type Props = {
  zipInsights: ZipInsightItem[];
  agents: MultiSelectOption[];
};

const ZipInsights = ({ zipInsights, agents }: Props) => {

  return (
    <div>
      <Accordion
        type="single"
        collapsible
        className={cn("w-full max-h-[38dvh] overflow-y-auto",
              '[&::-webkit-scrollbar]:w-2',
              // track color in WebKit
              '[&::-webkit-scrollbar-track]:bg-[#F4F4F5]',
              // thumb color + rounded in WebKit
              '[&::-webkit-scrollbar-thumb]:bg-[#09090B]',
              '[&::-webkit-scrollbar-thumb]:rounded-full',
              // Firefox: thin + color (thumb then track)
              '[scrollbar-width:thin]',
              '[scrollbar-color:#09090B_#F4F4F5]',
              {'px-0': Boolean(zipInsights?.[0]?.user_id)}
            )}
        defaultValue={String(zipInsights?.[0]?.user_id)}
      >
        {zipInsights?.map((eachZipInsight, index) => {
          const currentAgent = agents?.find(
            eachAgent =>
              Number(eachAgent?.value) === Number(eachZipInsight?.user_id),
          );

          if (currentAgent) {
            return (
              <div key={index}>
                <AccordionItem value={String(eachZipInsight?.user_id)}>
                  <AccordionTrigger>
                    <div className='flex items-center gap-2'>
                      <Avatar className="h-6 w-6">
                        {currentAgent?.avatarUrl && (
                          <AvatarImage
                            src={currentAgent?.avatarUrl}
                            alt={currentAgent?.label}
                          />
                        )}
                        <AvatarFallback>
                          {currentAgent.label
                            .split(' ')
                            .map(s => s[0])
                            .join('')
                            .toUpperCase()}
                        </AvatarFallback>
                      </Avatar>

                      <p className='text-heading font-medium'>{currentAgent?.label}</p>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="flex flex-col gap-4 text-balance">
                    {eachZipInsight?.insights?.map(eachInsight => {
                      const ZipInsightIcon =
                      InsightIcon[eachInsight?.sentiment as keyof typeof InsightIcon] ||
                      InsightIcon.NEUTRAL;

                      return (
                        <div
                          key={eachInsight?.sentiment}
                          className={cn("flex items-start gap-3 max-w-full mr-4 my-2 py-1", {'m-0 mr-4': Boolean(zipInsights?.[0]?.user_id)})}
                        >
                          <div className="w-4 h-4">
                            <ZipInsightIcon />
                          </div>

                          <p className="text-paragraph text-sm">
                            {eachInsight?.insight}
                          </p>
                        </div>
                      );
                    })}
                  </AccordionContent>
                </AccordionItem>
              </div>
            );
          }

          return eachZipInsight?.insights?.map(eachInsight => {
            const ZipInsightIcon = InsightIcon[eachInsight?.sentiment];

            return (
              <div
                key={eachInsight?.sentiment}
                className={cn("flex items-start gap-3 max-w-full mr-4 my-2 py-1", {'m-0 mr-4': Boolean(zipInsights?.[0]?.user_id)})}
              >
                <div className="w-4 h-4">
                  <ZipInsightIcon />
                </div>

                <p className="text-paragraph text-sm">{eachInsight?.insight}</p>
              </div>
            );
          });
        })}
      </Accordion>
    </div>
  );
};

export default ZipInsights;
